'use client'

import { useState, useTransition } from 'react'
import Link from 'next/link'
import posthog from 'posthog-js'
import type { Lang } from '@/lib/i18n'
import LangToggle from '@/components/lang-toggle'
import { submitJoinRequest, type JoinPlan } from './actions'

// Plan proper names (Esencial / Cuidado / Memoria total) stay untranslated;
// only the tagline switches with the language.
const PLAN_META: Array<{ id: JoinPlan; label: string }> = [
  { id: 'essential', label: 'Esencial'      },
  { id: 'premium',   label: 'Cuidado'       },
  { id: 'elite',     label: 'Memoria total' },
]

type Copy = {
  homeLink: string
  eyebrow: string
  headline: string
  subhead: string
  name: string
  email: string
  whatsapp: string
  location: string
  locationHelper: string
  planLabel: string
  taglines: Record<JoinPlan, string>
  submit: string
  sending: string
  footer: string
  doneEyebrow: string
  doneTitle: string
  doneBody: (planLabel: string) => string
  backHome: string
}

const COPY: Record<Lang, Copy> = {
  en: {
    homeLink: '← Home',
    eyebrow: 'Become a member',
    headline: 'A few details and we’ll reach out.',
    subhead: 'We use this to prepare your onboarding visit and a tailored quote. No obligation, no charge until you say go.',
    name: 'Full name',
    email: 'Email',
    whatsapp: 'WhatsApp',
    location: 'Property location',
    locationHelper: 'Neighborhood plus a landmark we can find.',
    planLabel: 'Plan you’re considering',
    taglines: {
      essential: 'Base preventive catalog · from $350/mo',
      premium:   'Esencial + deep care · from $550/mo',
      elite:     'Cuidado + weekly presence · from $900/mo',
    },
    submit: 'Send membership interest',
    sending: 'Sending…',
    footer: 'We respond within one business day. No automated drip — a person reads this.',
    doneEyebrow: 'Membership · Received',
    doneTitle: 'We got you.',
    doneBody: (planLabel) => `We’ll be in touch within one business day to walk you through the ${planLabel} plan and schedule the onboarding visit.`,
    backHome: 'Back to home',
  },
  es: {
    homeLink: '← Inicio',
    eyebrow: 'Hacete miembro',
    headline: 'Unos datos y te contactamos.',
    subhead: 'Los usamos para preparar tu visita de arranque y una cotización a tu medida. Sin compromiso, y sin cobro hasta que vos digas.',
    name: 'Nombre completo',
    email: 'Correo',
    whatsapp: 'WhatsApp',
    location: 'Ubicación de la propiedad',
    locationHelper: 'Barrio y un punto de referencia que podamos encontrar.',
    planLabel: 'Plan que estás considerando',
    taglines: {
      essential: 'Catálogo preventivo base · desde $350/mes',
      premium:   'Esencial + cuidado profundo · desde $550/mes',
      elite:     'Cuidado + presencia semanal · desde $900/mes',
    },
    submit: 'Enviar interés de membresía',
    sending: 'Enviando…',
    footer: 'Respondemos en un día hábil. Sin secuencias automáticas — lo lee una persona.',
    doneEyebrow: 'Membresía · Recibido',
    doneTitle: 'Listo, te tenemos.',
    doneBody: (planLabel) => `Te contactamos en un día hábil para explicarte el plan ${planLabel} y coordinar la visita de arranque.`,
    backHome: 'Volver al inicio',
  },
}

export default function JoinForm({ initialPlan, lang }: { initialPlan: JoinPlan; lang: Lang }) {
  const t = COPY[lang]
  const [plan, setPlan]    = useState<JoinPlan>(initialPlan)
  const [accepted, setAccepted] = useState(false)
  const [pending, startTransition] = useTransition()
  const [err, setErr]      = useState<string | null>(null)
  const [done, setDone]    = useState(false)

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setErr(null)
    if (!accepted) {
      setErr('Please accept the Terms & Conditions to continue.')
      return
    }
    const fd = new FormData(e.currentTarget)
    const email = String(fd.get('email') ?? '')
    startTransition(async () => {
      const res = await submitJoinRequest({
        name:     String(fd.get('name')     ?? ''),
        email,
        phone:    String(fd.get('phone')    ?? ''),
        location: String(fd.get('location') ?? ''),
        plan,
        acceptedTerms: accepted,
      })
      if (res.ok === false) {
        setErr(res.error)
        return
      }
      // Analytics: tie this lead's anonymous browsing session to their
      // email so the server-fired `lead_created` event lines up on the
      // same person profile.
      if (process.env.NEXT_PUBLIC_POSTHOG_KEY && email) {
        try {
          posthog.alias(email)
          posthog.capture('join_form_submitted', { plan })
        } catch {}
      }
      setDone(true)
    })
  }

  if (done) {
    return (
      <div className="min-h-screen bg-trc-cream flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl border border-trc-ash-line max-w-md w-full p-8 text-center shadow-card">
          <p className="text-[10px] font-bold text-trc-blue uppercase tracking-widest mb-2">
            {t.doneEyebrow}
          </p>
          <h1 className="text-2xl font-black text-trc-navy mb-3">{t.doneTitle}</h1>
          <p className="text-sm text-trc-navy mb-6 leading-relaxed">
            {t.doneBody(PLAN_META.find(p => p.id === plan)?.label ?? '')}
          </p>
          <Link
            href="/"
            className="inline-block px-6 py-3 rounded-pill bg-trc-blue text-white text-[0.72rem] font-bold uppercase tracking-label hover:bg-trc-blue-mid transition-colors"
          >
            {t.backHome}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-trc-cream">
      <div className="max-w-xl mx-auto px-4 py-10 md:py-16">
        <div className="flex items-center justify-between mb-6">
          <Link
            href="/"
            className="text-[0.72rem] font-bold text-trc-blue uppercase tracking-label hover:text-trc-navy transition-colors"
          >
            {t.homeLink}
          </Link>
          <LangToggle current={lang} />
        </div>

        <p className="text-[10px] font-bold text-trc-blue uppercase tracking-widest mb-2">
          {t.eyebrow}
        </p>
        <h1 className="text-3xl md:text-4xl font-black text-trc-navy leading-[1.1] mb-3">
          {t.headline}
        </h1>
        <p className="text-sm text-trc-ash mb-8 max-w-md leading-relaxed">
          {t.subhead}
        </p>

        <form
          onSubmit={onSubmit}
          className="bg-white rounded-2xl border border-trc-ash-line shadow-card p-6 md:p-8 space-y-5"
        >
          <Field label={t.name} required>
            <input
              type="text" name="name" required minLength={2}
              autoComplete="name"
              className="w-full px-3 py-2.5 rounded-xl border border-trc-ash-line text-sm focus:outline-none focus:border-trc-blue"
            />
          </Field>

          <Field label={t.email} required>
            <input
              type="email" name="email" required
              autoComplete="email"
              className="w-full px-3 py-2.5 rounded-xl border border-trc-ash-line text-sm focus:outline-none focus:border-trc-blue"
            />
          </Field>

          <Field label={t.whatsapp} required>
            <input
              type="tel" name="phone" required
              autoComplete="tel"
              placeholder="+506 8888 1234"
              className="w-full px-3 py-2.5 rounded-xl border border-trc-ash-line text-sm focus:outline-none focus:border-trc-blue"
            />
          </Field>

          <Field
            label={t.location}
            required
            helper={t.locationHelper}
          >
            <input
              type="text" name="location" required
              className="w-full px-3 py-2.5 rounded-xl border border-trc-ash-line text-sm focus:outline-none focus:border-trc-blue"
            />
          </Field>

          <Field label={t.planLabel}>
            <div className="grid grid-cols-3 gap-2">
              {PLAN_META.map(p => (
                <label
                  key={p.id}
                  className={`flex flex-col items-start text-left px-3 py-3 rounded-xl border cursor-pointer transition-all ${
                    plan === p.id
                      ? 'bg-trc-blue-pale border-trc-blue text-trc-blue'
                      : 'bg-white border-trc-ash-line text-trc-navy hover:border-trc-blue/40'
                  }`}
                >
                  <input
                    type="radio"
                    name="plan_radio"
                    value={p.id}
                    checked={plan === p.id}
                    onChange={() => setPlan(p.id)}
                    className="sr-only"
                  />
                  <span className="text-[0.72rem] font-bold uppercase tracking-label">
                    {p.label}
                  </span>
                  <span className="text-[10px] mt-1 text-trc-ash leading-tight">
                    {t.taglines[p.id]}
                  </span>
                </label>
              ))}
            </div>
          </Field>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={accepted}
              onChange={e => setAccepted(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-trc-ash-line text-trc-blue focus:ring-trc-blue"
            />
            <span className="text-[11px] text-trc-ash leading-snug">
              I have read and accept the{' '}
              <Link href="/terms" target="_blank" className="text-trc-blue hover:text-trc-navy underline">
                Terms &amp; Conditions
              </Link>{' '}
              and the{' '}
              <Link href="/privacy" target="_blank" className="text-trc-blue hover:text-trc-navy underline">
                Privacy Policy
              </Link>.
            </span>
          </label>

          {err && (
            <p className="text-sm text-trc-signal-red font-semibold">{err}</p>
          )}

          <button
            type="submit"
            disabled={pending || !accepted}
            className="w-full py-3 rounded-pill bg-trc-blue hover:bg-trc-blue-mid disabled:opacity-60 text-white text-[0.72rem] font-bold uppercase tracking-label transition-colors"
          >
            {pending ? t.sending : t.submit}
          </button>

          <p className="text-[11px] text-trc-ash text-center">
            {t.footer}
          </p>
        </form>
      </div>
    </div>
  )
}

function Field({
  label,
  required,
  helper,
  children,
}: {
  label:     string
  required?: boolean
  helper?:   string
  children:  React.ReactNode
}) {
  return (
    <div>
      <label className="block text-xs font-bold text-trc-navy mb-1">
        {label}
        {required && ' *'}
      </label>
      {children}
      {helper && <p className="text-[11px] text-trc-ash mt-1">{helper}</p>}
    </div>
  )
}
